import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { api } from "../../../api/apiService";
import DeleteModal from "../../../components/Modal/DeleteModal";
import RestoreUserModal from "../../../components/Modal/RestoreUserModal";
import ChangePasswordUserModal from "../../../components/Modal/ChangePasswordUserModal";

const schema = yup.object().shape({
  name: yup.string().required("name is required").min(3, "name must be at least 3 characters"),
  email: yup.string().required("Email is required").email("Email must be valid"),
  password: yup.string().when("isEditMode", {
    is: false,
    then: yup.string().required("Password is required").min(6, "Password must be at least 6 characters"),
  }),
  role: yup.string().required("Role is required"),
});

const Users = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [formData, setFormData] = useState({ password: "", confirmPassword: "" });
  const [errorMessage, setErrorMessage] = useState({});
  const [isRestoring, setIsRestoring] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("admin/users");
      setUsers(response.data);
      setIsLoading(false);
    } catch (error) {
      console.log("err", error);
      setIsLoading(false);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const openModal = () => {
    reset();
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (selectedUser) {
        await api.post(`admin/users/${selectedUser.id}`, data);
        setUsers(users.map((user) => (user.id === selectedUser.id ? { ...user, ...data } : user)));
        toast.success("User updated successfully");
      } else {
        const response = await api.post("admin/users", data);
        setUsers([response.data.user, ...users]);
        toast.success("User created successfully");
      }
      reset();
      closeModal();
    } catch (error) {
      if (error.data && error.data.errors) {
        error.data.errors.forEach(err => {
          toast.error(err);
        });
      } else {
        toast.error("An unexpected error occurred.Please try again");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setValue("name", user.name);
    setValue("email", user.email);
    setValue("role", user.role);
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleDeleteModal = (user) => {
    setSelectedUser(user);
    setDeleteModal(true);
  };

  const handleDeleteUser = async () => {
    setIsRestoring(true);
    try {
      await api.delete(`admin/users/${selectedUser?.id}`);
      const currentTimestamp = new Date().toISOString();

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === selectedUser?.id ? { ...user, deleted_at: currentTimestamp } : user
        )
      );
      toast.success("User deleted successfully");
    } catch (error) {
      if (error.data && error.data.errors) {
        error.data.errors.forEach(err => {
          toast.error(err);
        });
      } else {
        toast.error("Failed to delete user. Please try again");
      }
    } finally {
      setDeleteModal(false);
      setIsRestoring(false);
    }
  };

  const openRestoreModal = (user) => {
    setSelectedUser(user);
    setIsRestoreModalOpen(true);
  };

  const closeRestoreModal = () => {
    setSelectedUser(null);
    setIsRestoreModalOpen(false);
  };

  const confirmRestoreUser = async () => {
    setIsRestoring(true);
    try {
      const response = await api.get(`admin/users/${selectedUser.id}/restore`);

      toast.success(response.data.message);
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === selectedUser.id ? { ...user, deleted_at: null } : user
        )
      );
      closeRestoreModal();
    } catch (error) {
      if (error.data && error.data.errors) {
        error.data.errors.forEach(err => {
          toast.error(err);
        });
      } else {
        toast.error("An error occurred while restoring the user. Please try again");
      }
      closeRestoreModal();
    } finally {
      setIsRestoring(false);
    }
  };

  const openPasswordModal = (user) => {
    setFormData({});
    setErrorMessage({});
    setSelectedUser(user)
    setChangePasswordModal(true);
  };

  const closePasswordModal = () => {
    setFormData({});
    setErrorMessage({});
    setChangePasswordModal(false);
    setSelectedUser(null);
  };
  const handleChangeForm = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  const changePassword = async () => {
    // Validation
    const newErrors = {};
    if (!formData.password) {
      newErrors.password = "Password is required";
    }
    if (!formData.confirmPassword) {
      newErrors.password_confirmation = "Confirm password is required";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.password_confirmation = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrorMessage(newErrors);
      return;
    }

    try {
      setIsRestoring(true);
      const response = await api.post(`admin/users/${selectedUser.id}/change-password`, {
        password: formData.password,
        password_confirmation: formData.confirmPassword,
      });
      toast.success(response.data.message);
      closePasswordModal();
    } catch (error) {
      if (error.data && error.data.errors) {
        error.data.errors.forEach(err => {
          toast.error(err);
        });
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <main id="main" className="main">
      <div className="pagetitle">
        <h1>Users</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/user/meetings">Home</Link>
            </li>
            <li className="breadcrumb-item">Users</li>
          </ol>
        </nav>
      </div>
      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="col-12">
                  <h5 className="card-title" />
                  <Button className="btn btn-primary" onClick={openModal}>
                    Add User
                  </Button>
                  <div className="mt-4">
                    <div className="row">
                      <div className="col-md-8">
                        <h2>Users List</h2>
                        <table className="table">
                          <thead>
                            <tr>
                              <th scope="col">#</th>
                              <th scope="col">Name</th>
                              <th scope="col">Email</th>
                              <th scope="col">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users && users.map((user, index) => {
                              return (
                                <tr key={user?.id}>
                                  <td>{index + 1}</td>
                                  <td>{user?.name}</td>
                                  <td>{user?.email}</td>
                                  <td>
                                    <span
                                      title="Edit"
                                      className="text-primary me-2"
                                      style={{ cursor: "pointer" }}
                                      onClick={() => handleEditUser(user)}
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </span>

                                    <span
                                      title="Change Password"
                                      className="text-warning me-2"
                                      style={{ cursor: "pointer" }}
                                      onClick={() => openPasswordModal(user)}
                                    >
                                      <i className="bi bi-lock"></i>
                                    </span>

                                    {user.deleted_at === null &&
                                      <span
                                        title="Delete"
                                        className="text-danger me-2"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => handleDeleteModal(user)}
                                      >
                                        <i className="bi bi-trash"></i>
                                      </span>
                                    }

                                    {user.deleted_at && (
                                      <span
                                        title="Restore User"
                                        className="text-success me-2"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => openRestoreModal(user)}
                                      >
                                        <i className="bi bi-arrow-clockwise"></i>
                                      </span>
                                    )}

                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ADD/ EDIT USER*/}
      <Modal show={isModalOpen} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedUser ? "Edit User" : "Add New User"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-3">
              <label htmlFor="name" className="form-label">Name</label>
              <input
                type="text"
                className={`form-control ${errors.name ? "is-invalid" : ""}`}
                id="name"
                placeholder="Enter name"
                {...register("name")}
              />
              {errors.name && (
                <div className="invalid-feedback">{errors.name.message}</div>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="email" className="form-label">Email address</label>
              <input
                type="email"
                className={`form-control ${errors.email ? "is-invalid" : ""}`}
                id="email"
                placeholder="Enter email"
                {...register("email")}
              />
              {errors.email && (
                <div className="invalid-feedback">{errors.email.message}</div>
              )}
            </div>

            {!selectedUser && (
              <div className="mb-3">
                <label htmlFor="password" className="form-label">Password</label>
                <input
                  type="password"
                  className={`form-control ${errors.password ? "is-invalid" : ""}`}
                  id="password"
                  placeholder="Enter password"
                  {...register("password")}
                />
                {errors.password && (
                  <div className="invalid-feedback">{errors.password.message}</div>
                )}
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="role" className="form-label">Role</label>
              <select
                className={`form-control ${errors.role ? "is-invalid" : ""}`}
                id="role"
                {...register("role")}
              >
                <option value="">Select role</option>
                {/* <option value="admin">Admin</option> */}
                <option value="regular">Regular</option>
                <option value="viewer">Viewer</option>
              </select>
              {errors.role && (
                <div className="invalid-feedback">{errors.role.message}</div>
              )}
            </div>

            <Modal.Footer>
              <Button variant="secondary" onClick={closeModal}>
                Close
              </Button>
              <Button variant="primary" type="submit" disabled={isLoading}>
                {selectedUser ? "Update User" : "Save"}
              </Button>
            </Modal.Footer>
          </form>
        </Modal.Body>
      </Modal>

      {/* DELETE USER MODAL  */}
      <DeleteModal
        show={deleteModal}
        onClose={() => setDeleteModal(false)}
        onDelete={handleDeleteUser}
        isLoading={isRestoring}
        body={
          <>
            Are you sure you want to delete the user <strong className="text-capitalize">"{selectedUser?.name}"</strong>?
          </>
        }
      />

      {/* RESTORE USER MODAL  */}
      <RestoreUserModal
        isRestoreModalOpen={isRestoreModalOpen}
        selectedUser={selectedUser}
        closeRestoreModal={closeRestoreModal}
        confirmRestoreUser={confirmRestoreUser}
        isRestoring={isRestoring}
      />

      {/* Change PASSWORD*/}
      <ChangePasswordUserModal
        isRestoring={isRestoring}
        formData={formData}
        changePassword={changePassword}
        closePasswordModal={closePasswordModal}
        changePasswordModal={changePasswordModal}
        errorMessage={errorMessage}
        handleChangeForm={handleChangeForm}
      />


    </main>
  );
};

export default Users;
